import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Activity, AlertTriangle, Clock, Users, TrendingUp, Phone, Mail, Globe } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from "recharts";

const SLA_COLORS = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444" };
const SLA_LABELS = { green: "Até 1h", yellow: "1h - 4h", red: "Acima de 4h" };

export default function LeadsSLA() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/leads-sla/").then(r => setData(r.data));
    const t = setInterval(() => api.get("/leads-sla/").then(r => setData(r.data)), 30000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="text-gray-500">Carregando…</div>;

  const slaData = [
    { name: SLA_LABELS.green, value: data.sla_buckets.green, color: SLA_COLORS.green },
    { name: SLA_LABELS.yellow, value: data.sla_buckets.yellow, color: SLA_COLORS.yellow },
    { name: SLA_LABELS.red, value: data.sla_buckets.red, color: SLA_COLORS.red },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="admin-leads-sla">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">CRM · SLA</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Dashboard de Leads</h1>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-1.5"><Activity size={12} className="text-emerald-500" /> Atualiza a cada 30s</div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card" data-testid="kpi-today">
          <Users size={16} className="text-[#14213D] mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Leads hoje</div>
          <div className="font-display text-2xl text-[#14213D]">{data.today_count}</div>
          <div className="text-xs text-emerald-600 mt-1">{data.today_won} convertidos</div>
        </div>
        <div className="kpi-card" data-testid="kpi-response">
          <Clock size={16} className="text-[#14213D] mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Tempo médio resposta</div>
          <div className="font-display text-2xl text-[#14213D]">{data.avg_response_minutes != null ? `${data.avg_response_minutes} min` : "—"}</div>
          <div className="text-xs text-gray-400 mt-1">de {data.responded_count} leads</div>
        </div>
        <div className="kpi-card border-amber-300 bg-gradient-to-br from-white to-amber-50" data-testid="kpi-unattended">
          <AlertTriangle size={16} className="text-amber-600 mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Sem atendimento</div>
          <div className="font-display text-2xl text-amber-700">{data.unattended_count}</div>
          <div className="text-xs text-amber-600 mt-1">aguardando resposta</div>
        </div>
        <div className="kpi-card border-emerald-300" data-testid="kpi-conversion">
          <TrendingUp size={16} className="text-emerald-600 mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Origens ativas</div>
          <div className="font-display text-2xl text-emerald-700">{data.by_source.length}</div>
          <div className="text-xs text-emerald-600 mt-1">canais de captação</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* SLA Distribution */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">SLA dos não-atendidos</div>
          <div className="font-display text-xl text-[#14213D] mb-4">Distribuição por tempo</div>
          {slaData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-emerald-600 text-sm">
              <span className="bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2">Todos os leads foram atendidos 🎉</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={slaData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>
                  {slaData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Ranking by Source */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Ranking de origens</div>
          <div className="font-display text-xl text-[#14213D] mb-4">Leads por canal & conversão</div>
          {data.by_source.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">Sem dados ainda</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_source} layout="vertical">
                <CartesianGrid stroke="#F3F4F6" />
                <XAxis type="number" stroke="#9CA3AF" fontSize={11} />
                <YAxis type="category" dataKey="source" stroke="#9CA3AF" fontSize={11} width={100} />
                <Tooltip />
                <Bar dataKey="total" fill="#14213D" radius={[0, 6, 6, 0]} barSize={16} />
                <Bar dataKey="won" fill="#D4AF37" radius={[0, 6, 6, 0]} barSize={16} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Unattended list */}
      <div className="bg-white border border-amber-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-700" />
          <span className="font-semibold text-amber-900">Leads aguardando atendimento</span>
          <span className="text-xs text-amber-700 ml-auto">Mostrando {data.unattended.length} de {data.unattended_count}</span>
        </div>
        <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 font-semibold">
            <tr>
              <th className="px-5 py-3">SLA</th>
              <th className="px-5 py-3">Lead</th>
              <th className="px-5 py-3">Contato</th>
              <th className="px-5 py-3">Origem</th>
              <th className="px-5 py-3">Aguardando há</th>
            </tr>
          </thead>
          <tbody>
            {data.unattended.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-emerald-600">Nenhum lead pendente. Excelente trabalho! 🎉</td></tr>
            )}
            {data.unattended.map((l) => (
              <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`unattended-${l.id}`}>
                <td className="px-5 py-3">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: SLA_COLORS[l.sla] }} />
                    <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: SLA_COLORS[l.sla] }}>{SLA_LABELS[l.sla]}</span>
                  </span>
                </td>
                <td className="px-5 py-3 font-semibold text-[#14213D]">{l.name}</td>
                <td className="px-5 py-3 text-gray-600 text-xs">
                  {l.phone && <div className="flex items-center gap-1"><Phone size={11} />{l.phone}</div>}
                </td>
                <td className="px-5 py-3 text-gray-600 text-xs">{l.source || "—"}</td>
                <td className="px-5 py-3 text-xs">
                  <span className="font-semibold" style={{ color: SLA_COLORS[l.sla] }}>
                    {l.age_minutes < 60 ? `${Math.round(l.age_minutes)} min` :
                     l.age_minutes < 1440 ? `${Math.round(l.age_minutes / 60)}h` :
                     `${Math.round(l.age_minutes / 1440)} dias`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
